import React, { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import Navbar from "../components/Navbar";
import Footer from "../components/Footer";
import TiltCard from "../components/TiltCard";
import { api } from "../lib/api";

export default function ProjectPage() {
  const { slug } = useParams();
  const [project, setProject] = useState(null);
  const [projects, setProjects] = useState([]);
  const [loading, setLoading] = useState(true);
  const [notFound, setNotFound] = useState(false);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      setLoading(true);
      setNotFound(false);
      setProject(null);
      try {
        const [projectRes, projectsRes] = await Promise.all([
          api.get(`/projects/slug/${slug}`),
          api.get("/projects"),
        ]);
        if (cancelled) return;
        setProject(projectRes.data);
        setProjects(projectsRes.data || []);
      } catch (e) {
        if (cancelled) return;
        setNotFound(true);
      } finally {
        if (cancelled) return;
        setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [slug]);

  const currentIndex = projects.findIndex((p) => p.id === project?.id || p.slug === project?.slug);
  const previousProject = currentIndex > 0 ? projects[currentIndex - 1] : null;
  const nextProject =
    currentIndex >= 0 && currentIndex < projects.length - 1 ? projects[currentIndex + 1] : null;
  const relatedProjects = project
    ? [
        ...projects.filter(
          (p) => p.id !== project.id && p.section_id && p.section_id === project.section_id
        ),
        ...projects.filter(
          (p) => p.id !== project.id && (!project.section_id || p.section_id !== project.section_id)
        ),
      ].slice(0, 3)
    : [];

  return (
    <main data-testid="project-page">
      <Navbar />
      <div className="site-project-pad pt-32 pb-24">
        <div className="container">
          {loading && (
            <div className="text-bone-200/50 font-mono text-xs uppercase tracking-[0.3em] py-20 text-center">
              Loading…
            </div>
          )}

          {notFound && (
            <div className="py-20 text-center">
              <p className="font-display text-5xl mb-4">Project not found.</p>
              <Link
                to="/"
                className="font-mono text-xs uppercase tracking-[0.3em] text-ember"
              >
                ← Back to archive
              </Link>
            </div>
          )}

          {project && (
            <>
              <Link
                to="/"
                data-testid="back-to-home"
                className="site-button inline-flex items-center gap-3 font-mono text-[11px] uppercase tracking-[0.3em] text-bone-200/60 hover:text-ember transition-colors mb-12 border border-bone-50/10 px-4 py-3"
              >
                <span>←</span> Back to archive
              </Link>

              <div className="grid md:grid-cols-12 gap-12 items-end mb-16">
                <div className="md:col-span-8">
                  <div className="font-mono text-[10px] uppercase tracking-[0.3em] text-ember mb-4">
                    {project.location || project.section_name || "Project"} ·{" "}
                    {project.year || "—"}
                  </div>
                  <h1
                    data-testid="project-heading"
                    className="font-display text-5xl md:text-7xl tracking-tightest leading-[0.9] text-balance"
                  >
                    {project.title}
                  </h1>
                </div>
                <div className="md:col-span-4 text-bone-200/70 leading-relaxed text-sm md:text-base">
                  {project.description}
                </div>
              </div>

              {project.cover_url && (
                <div className="mb-8 overflow-hidden hairline bg-ink-700">
                  <img
                    src={project.cover_url}
                    alt={project.title}
                    className="w-full h-auto"
                    data-testid="project-cover"
                  />
                </div>
              )}

              {project.images && project.images.length > 0 && (
                <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
                  {project.images.map((img, i) => (
                    <div
                      key={img.id || i}
                      data-testid={`project-image-${i}`}
                      className={`overflow-hidden hairline bg-ink-700 ${
                        i % 3 === 0 ? "md:col-span-2" : ""
                      }`}
                    >
                      <img
                        src={img.url}
                        alt={img.caption || `${project.title} ${i + 1}`}
                        className="w-full h-auto"
                        loading="lazy"
                      />
                      {img.caption && (
                        <div className="p-4 font-mono text-[10px] uppercase tracking-[0.25em] text-bone-200/50">
                          {img.caption}
                        </div>
                      )}
                    </div>
                  ))}
                </div>
              )}

              {(previousProject || nextProject) && (
                <nav
                  aria-label="Project navigation"
                  className="mt-16 border-y border-bone-50/10 py-6 grid md:grid-cols-2 gap-4"
                  data-testid="project-prev-next"
                >
                  {previousProject ? (
                    <ProjectNavLink project={previousProject} label="Previous project" dir="prev" />
                  ) : (
                    <div />
                  )}
                  {nextProject && (
                    <ProjectNavLink project={nextProject} label="Next project" dir="next" />
                  )}
                </nav>
              )}

              {relatedProjects.length > 0 && (
                <section className="mt-20" data-testid="related-projects">
                  <div className="flex items-end justify-between gap-6 mb-8">
                    <div>
                      <div className="font-mono text-[10px] uppercase tracking-[0.3em] text-ember mb-3">
                        More work
                      </div>
                      <h2 className="font-display text-4xl tracking-tightest">
                        Related projects
                      </h2>
                    </div>
                    <Link
                      to="/#work"
                      className="site-button hidden md:inline-flex font-mono text-[11px] uppercase tracking-[0.25em] border border-bone-50/20 px-4 py-3 text-bone-200/70 hover:border-ember hover:text-ember"
                    >
                      View archive
                    </Link>
                  </div>
                  <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
                    {relatedProjects.map((item, i) => (
                      <TiltCard key={item.id || item.slug} project={item} index={i} />
                    ))}
                  </div>
                </section>
              )}
            </>
          )}
        </div>
      </div>
      <Footer />
    </main>
  );
}

function ProjectNavLink({ project, label, dir }) {
  const isNext = dir === "next";
  return (
    <Link
      to={`/project/${project.slug}`}
      data-testid={`project-${dir}`}
      className={`site-button group border border-bone-50/10 p-5 hover:border-ember transition-colors ${
        isNext ? "md:text-right" : ""
      }`}
    >
      <div className="font-mono text-[10px] uppercase tracking-[0.3em] text-bone-200/50 mb-2">
        {isNext ? "Next project" : label}
      </div>
      <div className="font-display text-2xl tracking-tightest group-hover:text-ember transition-colors">
        {!isNext && <span className="font-mono text-sm mr-2">←</span>}
        {project.title}
        {isNext && <span className="font-mono text-sm ml-2">→</span>}
      </div>
      <div className="font-mono text-[10px] uppercase tracking-[0.25em] text-bone-200/40 mt-2">
        {project.location || project.section_name || "Project"} · {project.year || "—"}
      </div>
    </Link>
  );
}
